// Client-side file-download helpers (Blob → <a download>) plus a CSV blob
// builder. Generic and portal-agnostic — shared by any exporter.

import { csvCell } from '@cca/safe';

/** Trigger a browser download of a Blob under `filename`. */
export function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Build a CSV blob from rows (UTF-8 BOM so Excel reads it correctly). Cells are
 *  encoded with @cca/safe's csvCell — RFC 4180 quoting + the SEC-2
 *  formula-injection guard (one escaper per sink, security.md SEC-3). */
export function csvBlob(rows: (string | number)[][]): Blob {
  const body = rows.map((row) => row.map(csvCell).join(',')).join('\r\n');
  return new Blob(['﻿', body], { type: 'text/csv;charset=utf-8' });
}

/** Today's date as YYYY-MM-DD **in NZ terms** (Pacific/Auckland), for filenames.
 *  UX-8: a file downloaded after midnight NZ must carry the NZ calendar day —
 *  `toISOString` is the UTC day, which reads "yesterday" until noon/1pm NZ.
 *  en-CA is the ISO-ordered locale; Intl is browser-native so this stays dep-free. */
export const stamp = (): string =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Pacific/Auckland',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
